import { assign, fromPromise, setup } from "xstate-5";
import { InjectionKey } from "vue";
import { useMachine } from "@xstate-5/vue";
import { AxiosError } from "axios";
import { getDataScienceSearchCustomer, getDataScienceSearchProducts } from "vue-app/api/DataScienceSearchService";
import { xlog } from "vue-app/utils/xstate";
import { logger } from "vue-app/utils/logger";
import {
  DTODataScienceSearchCustomerHit,
  DTODataScienceSearchCustomersResponse,
  DTODataScienceSearchProductHit,
  DTODataScienceSearchProductsResponse,
} from "vue-app/api/dto";
import { SEARCH_BAR_GROUP } from "./SearchBar.constants";

const LOCAL_STORAGE_KEY_RECENT_SEARCH = "SEARCH_BAR_RECENT_SEARCH";
const LOCAL_STORAGE_KEY_GROUP_BY = "SEARCH_BAR_GROUP_BY";

const getRecentSearchFromLocalStorage = () => {
  const items = localStorage.getItem(LOCAL_STORAGE_KEY_RECENT_SEARCH);
  if (items) {
    try {
      const parsedItems = JSON.parse(items);
      if (Array.isArray(parsedItems)) {
        return parsedItems;
      } else {
        logger({ level: "error", message: "Recent search items in localStorage is not an array", extra: { items } });
        return [];
      }
    } catch (error) {
      logger({ level: "error", message: "Error parsing recent search items from localStorage", extra: { error } });
      return [];
    }
  }
  return [];
};

const getGroupByDefaultValue = (): SEARCH_BAR_GROUP => {
  const value = localStorage.getItem(LOCAL_STORAGE_KEY_GROUP_BY);

  if (value === SEARCH_BAR_GROUP.CUSTOMERS || value === SEARCH_BAR_GROUP.PRODUCTS) {
    return value;
  }

  return SEARCH_BAR_GROUP.CUSTOMERS;
};

type SearchBarMachineContext = {
  searchQuery: string;
  recentSearch: string[];
  groupBy: SEARCH_BAR_GROUP;
  customers: {
    data: DTODataScienceSearchCustomerHit[];
    total: number;
    searchAfter: string | null;
  };
  products: {
    data: DTODataScienceSearchProductHit[];
    total: number;
    searchAfter: string | null;
  };
};

const initialContext: SearchBarMachineContext = {
  searchQuery: "",
  groupBy: getGroupByDefaultValue(),
  customers: {
    data: [],
    total: 0,
    searchAfter: null,
  },
  products: {
    data: [],
    total: 0,
    searchAfter: null,
  },
  recentSearch: getRecentSearchFromLocalStorage(),
};

export const searchBarMachine = setup({
  types: {
    tags: {} as any,
    context: {} as SearchBarMachineContext,
    events: {} as
      | { type: "ON_SEARCH_QUERY"; searchQuery: string }
      | { type: "FETCH_SEARCH_NEXT_PAGE" }
      | { type: "GROUP_BY"; groupBy: SEARCH_BAR_GROUP }
      | { type: "xstate.done.actor.fetchSearchResults"; output: DTODataScienceSearchCustomersResponse }
      | { type: "xstate.error.actor.fetchSearchResults"; error: AxiosError },
  },
  actions: {
    assignGroupBy: assign(({ event, context }, params: SEARCH_BAR_GROUP) => {
      xlog({ label: "assignGroupBy", type: "action", event, context, params });
      localStorage.setItem(LOCAL_STORAGE_KEY_GROUP_BY, params);

      return { groupBy: params };
    }),
    assignSearchQuery: assign(({ event, context }, params: string) => {
      xlog({ label: "assignSearchQuery", type: "action", event, context, params });

      return { searchQuery: params };
    }),
    assignSearchResultsForCustomers: assign(({ event, context }, params: DTODataScienceSearchCustomersResponse) => {
      xlog({ label: "assignSearchResultsForCustomers", type: "action", event, context, params });

      return {
        ...context,
        customers: {
          data: params.hits,
          total: params.total,
          searchAfter: params.searchAfter,
        },
      };
    }),
    assignSearchResultsForProducts: assign(({ event, context }, params: DTODataScienceSearchProductsResponse) => {
      xlog({ label: "assignSearchResultsForProducts", type: "action", event, context, params });

      return {
        ...context,
        products: {
          data: params.hits,
          total: params.total,
          searchAfter: params.searchAfter,
        },
      };
    }),
    assignNextPageSearchResultsForCustomers: assign(
      ({ event, context }, params: DTODataScienceSearchCustomersResponse) => {
        xlog({ label: "assignNextPageSearchResultsForCustomers", type: "action", event, context, params });

        return {
          ...context,
          customers: {
            data: [...context.customers.data, ...params.hits],
            searchAfter: params.searchAfter,
            total: params.total,
          },
        };
      }
    ),
    assignNextPageSearchResultsForProducts: assign(
      ({ event, context }, params: DTODataScienceSearchProductsResponse) => {
        xlog({ label: "assignNextPageSearchResultsForProducts", type: "action", event, context, params });

        return {
          ...context,
          products: {
            data: [...context.products.data, ...params.hits],
            searchAfter: params.searchAfter,
            total: params.total,
          },
        };
      }
    ),
    resetContext: assign(({ event, context }) => {
      xlog({ label: "resetContext", type: "action", event, context });

      return initialContext;
    }),
    saveRecentSearch: assign(({ event, context }, searchQuery: string) => {
      xlog({ label: "saveRecentSearch", type: "action", event, context, params: searchQuery });

      const recentSearch = context.recentSearch;
      const updatedRecentSearch = [...new Set([searchQuery, ...recentSearch])].slice(0, 15);

      localStorage.setItem(LOCAL_STORAGE_KEY_RECENT_SEARCH, JSON.stringify(updatedRecentSearch));

      return { recentSearch: updatedRecentSearch };
    }),
  },
  actors: {
    fetchCustomers: fromPromise(
      async ({ input }: { input: { searchQuery: string; searchAfter: string | null } }) =>
        await getDataScienceSearchCustomer(input.searchQuery, input.searchAfter)
    ),
    fetchProducts: fromPromise(
      async ({ input }: { input: { searchQuery: string; searchAfter: string | null } }) =>
        await getDataScienceSearchProducts(input.searchQuery, input.searchAfter)
    ),
  },
  guards: {
    isSearchQueryNotEmpty: ({ event }) => {
      if (event.type === "ON_SEARCH_QUERY") {
        return event.searchQuery.length > 0;
      }

      return false;
    },
    shouldFetchNextSearchPageCustomers: ({ context }) => {
      if (context.groupBy === SEARCH_BAR_GROUP.CUSTOMERS) {
        return true;
      }

      return false;
    },
    shouldFetchNextSearchPageProducts: ({ context }) => {
      if (context.groupBy === SEARCH_BAR_GROUP.PRODUCTS) {
        return true;
      }

      return false;
    },
    shouldFetchNextSearchPage: ({ event, context }) => {
      if (event.type === "FETCH_SEARCH_NEXT_PAGE") {
        return context.customers.searchAfter !== null || context.products.searchAfter !== null;
      }

      return false;
    },
  },
}).createMachine({
  context: initialContext,
  id: "searchBarMachine",
  initial: "idle",
  on: {
    ON_SEARCH_QUERY: [
      {
        guard: "isSearchQueryNotEmpty",
        target: ".fetchRequiredData",
        actions: [
          {
            type: "saveRecentSearch",
            params: ({ event }) => event.searchQuery,
          },
          {
            type: "assignSearchQuery",
            params: ({ event }) => event.searchQuery,
          },
        ],
      },
      {
        target: ".idle",
        actions: "resetContext",
      },
    ],
  },
  states: {
    idle: {
      on: {
        FETCH_SEARCH_NEXT_PAGE: [
          {
            target: "fetchNextPageData",
            guard: "shouldFetchNextSearchPage",
          },
          {
            target: "idle",
          },
        ],
        GROUP_BY: {
          actions: { type: "assignGroupBy", params: ({ event }) => event.groupBy },
          target: "idle",
        },
      },
    },
    fetchRequiredData: {
      type: "parallel",
      states: {
        fetchingSearchResultsForProducts: {
          initial: "fetchSearchResultsForProducts",
          states: {
            fetchSearchResultsForProducts: {
              invoke: {
                src: "fetchProducts",
                id: "fetchProducts",
                input: ({ context }) => {
                  return { searchQuery: context.searchQuery, searchAfter: null };
                },
                onDone: [
                  {
                    target: "done",
                    actions: [
                      {
                        type: "assignSearchResultsForProducts",
                        params: ({ event }) => event.output,
                      },
                    ],
                  },
                ],
                onError: {
                  target: "done",
                },
              },
            },
            done: {
              type: "final",
            },
          },
        },
        fetchingSearchResultsForCustomers: {
          initial: "fetchSearchResultsForCustomers",
          states: {
            fetchSearchResultsForCustomers: {
              invoke: {
                src: "fetchCustomers",
                id: "fetchCustomers",
                input: ({ context }) => {
                  return { searchQuery: context.searchQuery, searchAfter: null };
                },
                onDone: [
                  {
                    target: "done",
                    actions: [
                      {
                        type: "assignSearchResultsForCustomers",
                        params: ({ event }) => event.output,
                      },
                    ],
                  },
                ],
                onError: {
                  target: "done",
                },
              },
            },
            done: {
              type: "final",
            },
          },
        },
      },
      onDone: [
        {
          target: "idle",
        },
      ],
    },
    fetchNextPageData: {
      initial: "decideGroupBy",
      states: {
        decideGroupBy: {
          always: [
            {
              target: "fetchNextPageSearchResultsForProducts",
              guard: "shouldFetchNextSearchPageProducts",
            },
            {
              target: "fetchNextPageSearchResultsForCustomers",
              guard: "shouldFetchNextSearchPageCustomers",
            },
            { target: "done" },
          ],
        },
        fetchNextPageSearchResultsForProducts: {
          invoke: {
            src: "fetchProducts",
            id: "fetchProducts",
            input: ({ context }) => {
              return { searchQuery: context.searchQuery, searchAfter: context.products.searchAfter };
            },
            onDone: [
              {
                target: "done",
                actions: [
                  {
                    type: "assignNextPageSearchResultsForProducts",
                    params: ({ event }) => event.output,
                  },
                ],
              },
            ],
            onError: {
              target: "done",
            },
          },
        },
        fetchNextPageSearchResultsForCustomers: {
          invoke: {
            src: "fetchCustomers",
            id: "fetchCustomers",
            input: ({ context }) => {
              return { searchQuery: context.searchQuery, searchAfter: context.customers.searchAfter };
            },
            onDone: [
              {
                target: "done",
                actions: [
                  {
                    type: "assignNextPageSearchResultsForCustomers",
                    params: ({ event }) => event.output,
                  },
                ],
              },
            ],
            onError: {
              target: "done",
            },
          },
        },
        done: {
          type: "final",
        },
      },
      onDone: [
        {
          target: "idle",
        },
      ],
    },
  },
});

export const symbolSearchBarMachine: InjectionKey<ReturnType<typeof useMachine<typeof searchBarMachine>>> = Symbol();
